import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import '@chancenlogistik/client/components/logistik-component-base-header';
import '@chancenlogistik/client/components/logistik-component-base-page-layout';
import { LogistikRoutes } from '@chancenlogistik/client/routes.js';
import {
  AdvertisingMaterial,
  ChancenBrief,
  EventTicket,
  Furnishing,
  LearningMaterial,
  LogistikProduct,
  LogistikProductSet,
  TravelCertificate,
  WorkMaterial,
  WorkshopPack,
} from '@chancenwerk/db/LogistikProduct.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-grid.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LogistikDialogEditProduct } from '../components/dialogs/logistik-dialog-edit-product.js';

/**
 * @customElement logistik-page-products
 */
@customElement('logistik-page-products')
export class LogistikPageProducts extends ChemistryLitElement {
  override render() {
    return permissionGuard(ChancenPermission.logistik.LOGIN_LOGISTIK, () => this._content(), {
      forbiddenTemplate: html`
        <logistik-component-base-page-layout>
          <cr-flex ${css({ height: '100%' })} center crossaxis-center>
            <campus-page-no-permission></campus-page-no-permission>
          </cr-flex>
        </logistik-component-base-page-layout>
      `,
      context: ANY_CONTEXT,
    });
  }

  private _content() {
    return html`
      <logistik-component-base-page-layout>
        <logistik-component-base-header pageTitle="Hinzufügen" backRoute=${LogistikRoutes.landing}>
        </logistik-component-base-header>
        <cr-grid
          horizontal
          space="md"
          auto-fill-columns="250px"
          ${acss({
            'cr-card': { cursor: 'pointer', background: this.colors.accent, color: this.colors.light },
            'cr-icon': { fontSize: this.spacing.xl },
          })}
        >
          ${this._card({ title: 'CHANCENheft', icon: 'library_books', newProduct: () => new LearningMaterial({}) })}
          ${this._card({
            title: 'Werbe- und Akquisematerial',
            icon: 'campaign',
            newProduct: () => new AdvertisingMaterial({}),
          })}
          ${this._card({ title: 'Büromaterial', icon: 'work', newProduct: () => new WorkMaterial({}) })}
          ${this._card({ title: 'Eintrittskarten', icon: 'event', newProduct: () => new EventTicket({}) })}
          ${this._card({ title: 'Mobiliar', icon: 'weekend', newProduct: () => new Furnishing({}) })}
          ${this._card({
            title: 'CHANCENbrief',
            icon: 'history_edu',
            newProduct: () => new ChancenBrief({}),
          })}
          ${this._card({
            title: 'CHANCENreise-Zertifikat',
            icon: 'explore',
            newProduct: () => new TravelCertificate({}),
          })}
          ${this._card({
            title: 'CHANCENakademie Workshop-Pakete',
            icon: 'inventory_2',
            newProduct: () => new WorkshopPack({}),
          })}
          ${this._card({ title: 'CHANCENset', icon: 'package_2', newProduct: () => new LogistikProductSet({}) })}
        </cr-grid>
      </logistik-component-base-page-layout>
    `;
  }

  private _card<T extends LogistikProduct>({
    title,
    icon,
    newProduct,
  }: {
    title: string;
    icon: string;
    newProduct: () => T;
  }): Renderable {
    return html`
      <cr-card
        href="#"
        @click=${() => LogistikDialogEditProduct.showPopup({ modal: true, props: { product: newProduct() } })}
        ${css({ minHeight: '100px' })}
      >
        <cr-flex space="lg" crossaxis-center align="center">
          <cr-icon> ${icon} </cr-icon>
          <cr-text> ${title} </cr-text>
        </cr-flex>
      </cr-card>
    `;
  }
}
